import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { isLegalAcceptanceUiEnabled, safeString } from "../config/legalAcceptance";

const LegalAcceptanceContext = createContext(null);

const LEGAL_ERROR_CODES = new Set([
  "LEGAL_ACCEPTANCE_IMPERSONATION_FORBIDDEN",
  "LEGAL_ACCEPTANCE_COPY_NOT_CONFIGURED",
  "LEGAL_ACCEPTANCE_NOT_REQUIRED",
  "LEGAL_ACCEPTANCE_STALE",
  "LEGAL_ACCEPTANCE_IDEMPOTENCY_REUSE",
  "LEGAL_ACCEPTANCE_CONFIGURATION_INVALID",
  "LEGAL_ACCEPTANCE_STORE_FORBIDDEN",
  "LEGAL_ACCEPTANCE_INVALID_REQUEST",
  "LEGAL_ACCEPTANCE_FAILED",
]);

const DOCUMENT_ORDER = ["terms", "privacy"];
const ACK_BY_TYPE = {
  terms: "agree",
  privacy: "acknowledge",
};

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeHex(value) {
  return safeString(value).trim().toLowerCase();
}

function isSha256(value) {
  return /^[0-9a-f]{64}$/i.test(safeString(value).trim());
}

function isHttpsUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function isSafeLegalUrl(value) {
  const raw = safeString(value).trim();
  if (!raw) return false;
  if (raw.includes("\\")) return false;
  if (raw.startsWith("//")) return false;
  if (raw.startsWith("/")) {
    if (!/^\/(?!\/)/.test(raw)) return false;
    try {
      const base = "https://flowtrak.local";
      const parsed = new URL(raw, base);
      return parsed.origin === base;
    } catch {
      return false;
    }
  }
  return isHttpsUrl(raw);
}

function validateCopy(copy) {
  if (copy === null) return null;
  if (!isPlainObject(copy)) throw new Error("Invalid legal status response.");

  const copyVersion = safeString(copy.copyVersion).trim();
  const checkboxText = safeString(copy.checkboxText).trim();
  const arbitrationNoticeText =
    copy.arbitrationNoticeText == null
      ? null
      : safeString(copy.arbitrationNoticeText).trim();

  if (!copyVersion || !checkboxText) {
    throw new Error("Invalid legal status response.");
  }

  return {
    copyVersion,
    checkboxText,
    arbitrationNoticeText: arbitrationNoticeText || null,
  };
}

function normalizeRequiredDocuments(requiredDocuments) {
  if (!Array.isArray(requiredDocuments)) {
    throw new Error("Invalid legal status response.");
  }

  const seen = new Set();
  const docs = requiredDocuments.map((doc) => {
    if (!isPlainObject(doc)) throw new Error("Invalid legal status response.");

    const documentType = safeString(doc.documentType).trim();
    const publicVersion = safeString(doc.publicVersion).trim();
    const title = safeString(doc.title).trim();
    const contentSha256 = normalizeHex(doc.contentSha256);
    const publicUrl = safeString(doc.publicUrl).trim();
    const pdfUrl = doc.pdfUrl == null ? null : safeString(doc.pdfUrl).trim();
    const acknowledgmentType = safeString(doc.acknowledgmentType).trim();

    if (!DOCUMENT_ORDER.includes(documentType) || seen.has(documentType)) {
      throw new Error("Invalid legal status response.");
    }
    seen.add(documentType);

    if (!publicVersion || !title || !isSha256(contentSha256)) {
      throw new Error("Invalid legal status response.");
    }

    if (ACK_BY_TYPE[documentType] !== acknowledgmentType) {
      throw new Error("Invalid legal status response.");
    }

    if (!isSafeLegalUrl(publicUrl) || (pdfUrl !== null && !isSafeLegalUrl(pdfUrl))) {
      throw new Error("Invalid legal status response.");
    }

    return {
      documentType,
      publicVersion,
      title,
      effectiveDate: doc.effectiveDate || null,
      publishedAt: doc.publishedAt || null,
      contentSha256,
      publicUrl,
      pdfUrl: pdfUrl || null,
      acknowledgmentType,
      acceptanceRequiredFrom: doc.acceptanceRequiredFrom || null,
    };
  });

  return docs.sort(
    (a, b) =>
      DOCUMENT_ORDER.indexOf(a.documentType) - DOCUMENT_ORDER.indexOf(b.documentType)
  );
}

export function normalizeLegalStatus(data) {
  if (!isPlainObject(data) || data.ok !== true) {
    throw new Error("Invalid legal status response.");
  }

  const requiredBooleans = [
    "enforcementEnabled",
    "configurationReady",
    "isImpersonating",
    "canAccept",
    "wouldRequireAcceptance",
    "mustAcceptNow",
    "requirementsSatisfied",
  ];

  for (const key of requiredBooleans) {
    if (typeof data[key] !== "boolean") {
      throw new Error("Invalid legal status response.");
    }
  }

  if (data.currentAcceptance !== null && !isPlainObject(data.currentAcceptance)) {
    throw new Error("Invalid legal status response.");
  }

  const requiredDocuments = normalizeRequiredDocuments(data.requiredDocuments);
  const copy = validateCopy(data.copy);
  const currentAcceptance = data.currentAcceptance;
  const reason = safeString(data.reason).trim();
  const hasRequiredDocuments = requiredDocuments.length > 0;
  const expectedWouldRequireAcceptance =
    hasRequiredDocuments && !data.requirementsSatisfied;
  const expectedCanAccept =
    data.configurationReady &&
    hasRequiredDocuments &&
    expectedWouldRequireAcceptance &&
    !data.isImpersonating;
  const expectedMustAcceptNow =
    data.enforcementEnabled &&
    data.configurationReady &&
    expectedWouldRequireAcceptance &&
    !data.isImpersonating;

  if ((copy !== null) !== data.configurationReady) {
    throw new Error("Invalid legal status response.");
  }

  if (
    data.wouldRequireAcceptance !== expectedWouldRequireAcceptance ||
    data.canAccept !== expectedCanAccept ||
    data.mustAcceptNow !== expectedMustAcceptNow
  ) {
    throw new Error("Invalid legal status response.");
  }

  if (!hasRequiredDocuments) {
    if (
      data.requirementsSatisfied !== true ||
      data.wouldRequireAcceptance !== false ||
      currentAcceptance !== null ||
      reason !== "NO_REQUIRED_DOCUMENTS"
    ) {
      throw new Error("Invalid legal status response.");
    }
  } else if (currentAcceptance === null) {
    if (
      data.requirementsSatisfied !== false ||
      data.wouldRequireAcceptance !== true ||
      reason !== "ACCEPTANCE_REQUIRED"
    ) {
      throw new Error("Invalid legal status response.");
    }
  } else if (
    data.requirementsSatisfied !== true ||
    data.wouldRequireAcceptance !== false ||
    reason !== "REQUIREMENTS_SATISFIED"
  ) {
    throw new Error("Invalid legal status response.");
  }

  if (data.isImpersonating && (data.canAccept || data.mustAcceptNow)) {
    throw new Error("Invalid legal status response.");
  }

  return {
    ok: true,
    enforcementEnabled: data.enforcementEnabled,
    configurationReady: data.configurationReady,
    isImpersonating: data.isImpersonating,
    canAccept: data.canAccept,
    wouldRequireAcceptance: data.wouldRequireAcceptance,
    mustAcceptNow: data.mustAcceptNow,
    requirementsSatisfied: data.requirementsSatisfied,
    reason,
    copy,
    context: isPlainObject(data.context)
      ? {
          storeId: data.context.storeId || null,
          employmentId: data.context.employmentId || null,
        }
      : { storeId: null, employmentId: null },
    requiredDocuments,
    currentAcceptance,
  };
}

export function createRequirementFingerprint(status) {
  if (!status?.copy?.copyVersion || !Array.isArray(status?.requiredDocuments)) {
    return "";
  }

  const docs = normalizeRequiredDocuments(status.requiredDocuments);
  return JSON.stringify({
    copyVersion: status.copy.copyVersion,
    documents: docs.map((doc) => [
      doc.documentType,
      doc.publicVersion,
      normalizeHex(doc.contentSha256),
      doc.acknowledgmentType,
    ]),
  });
}

async function readJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function sanitizedMessageFor(status, data) {
  const code = safeString(data?.code).trim();
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (code && LEGAL_ERROR_CODES.has(code)) {
    return "Legal acceptance is not available right now. Please try again.";
  }
  if (status >= 500) {
    return "Legal acceptance is temporarily unavailable. Please try again.";
  }
  return "Legal acceptance request failed. Please try again.";
}

export class LegalAcceptanceApiError extends Error {
  constructor(message, { status = 0, code = "", data = null } = {}) {
    super(message);
    this.name = "LegalAcceptanceApiError";
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

export async function legalApiRequest({
  auth,
  path,
  method = "GET",
  token,
  storeId,
  body,
  signal,
}) {
  const url = auth.apiUrl(path);
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  if (safeString(storeId).trim()) headers["X-Store-Id"] = safeString(storeId).trim();
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const response = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: "no-store",
    signal,
  });
  const data = await readJsonSafely(response);

  if (!response.ok) {
    const code = safeString(data?.code).trim();
    throw new LegalAcceptanceApiError(sanitizedMessageFor(response.status, data), {
      status: response.status,
      code,
      data,
    });
  }

  return data;
}

export function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
    12,
    16
  )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function userIdentity(auth) {
  return safeString(auth?.user?.id || auth?.user?.userId || auth?.user?.email).trim();
}

function clearSession(auth) {
  if (typeof auth?.signOut === "function") auth.signOut();
  else if (typeof auth?.clearSession === "function") auth.clearSession();
}

export function LegalAcceptanceProvider({ children }) {
  const auth = useAuth();
  const uiEnabled = isLegalAcceptanceUiEnabled();
  const requestGeneration = useRef(0);
  const abortRef = useRef(null);
  const submittingRef = useRef(false);
  const idempotencyRef = useRef({ fingerprint: "", key: "" });
  const sessionKey = useMemo(
    () =>
      [
        safeString(auth?.token).trim(),
        safeString(auth?.effectiveStoreId).trim(),
        userIdentity(auth),
        auth?.user?.isImpersonating ? "impersonating" : "direct",
      ].join("|"),
    [auth]
  );

  const [phase, setPhase] = useState("idle");
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const requirementFingerprint = useMemo(
    () => (status ? createRequirementFingerprint(status) : ""),
    [status]
  );

  const resetIdempotencyKey = useCallback(() => {
    idempotencyRef.current = { fingerprint: "", key: "" };
  }, []);

  const refreshStatus = useCallback(async () => {
    const token = safeString(auth?.token).trim();
    const hydrated = !!auth?.hydrated;
    const isAuthenticated = !!auth?.isAuthenticated;
    const storeId = safeString(auth?.effectiveStoreId).trim();

    if (!hydrated || !isAuthenticated || !token || typeof auth?.apiUrl !== "function") {
      abortRef.current?.abort?.();
      requestGeneration.current += 1;
      setPhase("idle");
      setStatus(null);
      setStatusError(null);
      setSubmitError(null);
      resetIdempotencyKey();
      return null;
    }

    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;
    const generation = requestGeneration.current + 1;
    requestGeneration.current = generation;

    setPhase("loading");
    setStatusError(null);

    try {
      const data = await legalApiRequest({
        auth,
        path: "/api/legal/status",
        method: "GET",
        token,
        storeId,
        signal: controller.signal,
      });
      const normalized = normalizeLegalStatus(data);
      if (requestGeneration.current !== generation || controller.signal.aborted) {
        return null;
      }
      setStatus(normalized);
      setStatusError(null);
      setPhase("ready");
      return normalized;
    } catch (error) {
      if (controller.signal.aborted || requestGeneration.current !== generation) {
        return null;
      }
      if (error?.status === 401) clearSession(auth);
      const safeError =
        error instanceof LegalAcceptanceApiError
          ? error
          : new LegalAcceptanceApiError(
              "Legal acceptance status could not be loaded. Please try again.",
              { status: 0, code: "" }
            );
      setStatus(null);
      setStatusError(safeError);
      setPhase("error");
      return null;
    }
  }, [auth, resetIdempotencyKey]);

  useEffect(() => {
    refreshStatus();
    return () => {
      abortRef.current?.abort?.();
    };
  }, [refreshStatus, sessionKey]);

  useEffect(() => {
    resetIdempotencyKey();
    setSubmitError(null);
  }, [resetIdempotencyKey, requirementFingerprint, sessionKey]);

  const acceptCurrentRequirements = useCallback(async () => {
    if (submittingRef.current) return null;
    if (!status || !requirementFingerprint || !status.copy || !status.canAccept) {
      const error = new LegalAcceptanceApiError(
        "Legal acceptance is not ready. Please review the latest status.",
        { status: 0, code: "LEGAL_ACCEPTANCE_INVALID_REQUEST" }
      );
      setSubmitError(error);
      throw error;
    }
    if (status.isImpersonating) {
      const error = new LegalAcceptanceApiError(
        "Impersonated sessions cannot accept legal documents.",
        { status: 403, code: "LEGAL_ACCEPTANCE_IMPERSONATION_FORBIDDEN" }
      );
      setSubmitError(error);
      throw error;
    }

    const token = safeString(auth?.token).trim();
    if (!token) {
      const error = new LegalAcceptanceApiError("Your session has expired.", {
        status: 401,
        code: "",
      });
      setSubmitError(error);
      clearSession(auth);
      throw error;
    }

    if (idempotencyRef.current.fingerprint !== requirementFingerprint) {
      idempotencyRef.current = {
        fingerprint: requirementFingerprint,
        key: createIdempotencyKey(),
      };
    } else if (!idempotencyRef.current.key) {
      idempotencyRef.current.key = createIdempotencyKey();
    }

    const body = {
      idempotencyKey: idempotencyRef.current.key,
      copyVersion: status.copy.copyVersion,
      documents: status.requiredDocuments.map((doc) => ({
        documentType: doc.documentType,
        publicVersion: doc.publicVersion,
        contentSha256: normalizeHex(doc.contentSha256),
        acknowledgmentType: doc.acknowledgmentType,
      })),
    };

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const result = await legalApiRequest({
        auth,
        path: "/api/legal/acceptance",
        method: "POST",
        token,
        storeId: safeString(auth?.effectiveStoreId).trim(),
        body,
      });

      resetIdempotencyKey();
      setSubmitError(null);
      await refreshStatus();
      return result;
    } catch (error) {
      if (error?.status === 401) {
        resetIdempotencyKey();
        clearSession(auth);
      }

      if (
        error?.code === "LEGAL_ACCEPTANCE_STALE" ||
        error?.code === "LEGAL_ACCEPTANCE_IDEMPOTENCY_REUSE"
      ) {
        resetIdempotencyKey();
        await refreshStatus();
      } else if (
        error?.code === "LEGAL_ACCEPTANCE_NOT_REQUIRED" ||
        error?.code === "LEGAL_ACCEPTANCE_IMPERSONATION_FORBIDDEN"
      ) {
        await refreshStatus();
      }

      const safeError =
        error instanceof LegalAcceptanceApiError
          ? error
          : new LegalAcceptanceApiError(
              "Legal acceptance could not be submitted. Please try again.",
              { status: 0, code: "" }
            );
      setSubmitError(safeError);
      throw safeError;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [auth, refreshStatus, requirementFingerprint, resetIdempotencyKey, status]);

  const value = useMemo(
    () => ({
      uiEnabled,
      phase,
      status,
      statusError,
      submitting,
      submitError,
      refreshStatus,
      acceptCurrentRequirements,
      requirementFingerprint,
    }),
    [
      uiEnabled,
      phase,
      status,
      statusError,
      submitting,
      submitError,
      refreshStatus,
      acceptCurrentRequirements,
      requirementFingerprint,
    ]
  );

  return (
    <LegalAcceptanceContext.Provider value={value}>
      {children}
    </LegalAcceptanceContext.Provider>
  );
}

export function useLegalAcceptance() {
  const value = useContext(LegalAcceptanceContext);
  if (!value) {
    throw new Error("useLegalAcceptance must be used inside LegalAcceptanceProvider.");
  }
  return value;
}
